/**
 * Hala CRM - Operational Dashboard
 * Real-time visibility and control of requests for supervisors and admins
 */

import { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  LayoutDashboard, FileText, Inbox, UserCheck, CheckCircle, AlertTriangle,
  Clock, RefreshCw, Search, Filter, User, ChevronRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = {
  primary: '#1B3A6B',
  gold: '#C5A572',
  success: '#10B981',
  successLight: '#D1FAE5',
  warning: '#F59E0B',
  warningLight: '#FEF3C7',
  error: '#EF4444',
  errorLight: '#FEE2E2',
  info: '#3B82F6',
  infoLight: '#DBEAFE',
  white: '#FFFFFF',
  bg: '#F8FAFC',
  bgDark: '#F1F5F9',
  border: '#E2E8F0',
  text: '#1E293B',
  textMuted: '#64748B',
};

const STATUS_CONFIG = {
  NEW: { label: 'جديد', color: COLORS.info, bg: COLORS.infoLight },
  IN_POOL: { label: 'في المجمع', color: COLORS.warning, bg: COLORS.warningLight },
  ASSIGNED: { label: 'مُسند', color: COLORS.primary, bg: `${COLORS.primary}20` },
  IN_PROGRESS: { label: 'قيد التنفيذ', color: COLORS.gold, bg: `${COLORS.gold}20` },
  COMPLETED: { label: 'مكتمل', color: COLORS.success, bg: COLORS.successLight },
  CANCELLED: { label: 'ملغي', color: COLORS.error, bg: COLORS.errorLight },
};

function KPICard({ title, count, icon: Icon, color, bgColor, active, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        backgroundColor: active ? color : COLORS.white,
        padding: '20px',
        borderRadius: '12px',
        border: `2px solid ${active ? color : COLORS.border}`,
        cursor: 'pointer',
        transition: 'all 0.2s',
        flex: 1,
        minWidth: '150px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '12px',
          backgroundColor: active ? 'rgba(255,255,255,0.2)' : bgColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Icon size={24} color={active ? COLORS.white : color} />
        </div>
        <div>
          <p style={{ 
            margin: 0, 
            fontSize: '13px', 
            color: active ? 'rgba(255,255,255,0.8)' : COLORS.textMuted 
          }}>
            {title}
          </p>
          <p style={{ 
            margin: '4px 0 0', 
            fontSize: '28px', 
            fontWeight: 700, 
            color: active ? COLORS.white : color 
          }}>
            {count}
          </p>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || { label: status, color: COLORS.textMuted, bg: COLORS.bgDark };
  
  return (
    <span style={{
      display: 'inline-block',
      padding: '4px 10px',
      borderRadius: '20px',
      backgroundColor: config.bg,
      color: config.color,
      fontSize: '12px',
      fontWeight: 500,
    }}>
      {config.label}
    </span>
  );
}

export default function CRMDashboardPage() {
  const { user, getCRMHeaders } = useOutletContext();
  const navigate = useNavigate();
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState(null); // 'new', 'pool', 'assigned', 'completed'
  const [filters, setFilters] = useState({
    program: '',
    service: '',
    status: '',
  });

  const fetchDashboard = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    
    try {
      const params = new URLSearchParams();
      if (filters.program) params.append('program', filters.program);
      if (filters.service) params.append('service', filters.service);
      if (filters.status) params.append('status', filters.status);
      
      const res = await axios.get(
        `${API}/requests/dashboard/${user.user_id}?${params}`,
        { headers: getCRMHeaders() }
      );
      setData(res.data);
    } catch (e) {
      console.error('Failed to fetch dashboard:', e);
    } finally {
      setLoading(false);
    }
  }, [user, filters, getCRMHeaders]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // Apply KPI filter to requests
  const getFilteredRequests = () => {
    if (!data?.requests) return [];
    
    let requests = data.requests;
    
    if (activeFilter === 'new') {
      requests = requests.filter(r => r.status === 'NEW');
    } else if (activeFilter === 'pool') {
      requests = requests.filter(r => r.status === 'IN_POOL');
    } else if (activeFilter === 'assigned') {
      requests = requests.filter(r => ['ASSIGNED', 'IN_PROGRESS'].includes(r.status));
    } else if (activeFilter === 'completed') {
      requests = requests.filter(r => r.status === 'COMPLETED');
    }
    
    return requests;
  };

  const handleKPIClick = (filterType) => {
    setActiveFilter(activeFilter === filterType ? null : filterType);
    // Reset status filter when using KPI cards
    setFilters(prev => ({ ...prev, status: '' }));
  };

  const filteredRequests = getFilteredRequests();

  if (loading && !data) {
    return (
      <div style={{ padding: '60px', textAlign: 'center', color: COLORS.textMuted, direction: 'rtl' }}>
        جاري التحميل...
      </div>
    );
  }

  return (
    <div style={{ direction: 'rtl' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ margin: '0 0 8px', fontSize: '24px', fontWeight: 700, color: COLORS.text }}>
            لوحة التحكم
          </h1>
          <p style={{ margin: 0, fontSize: '14px', color: COLORS.textMuted }}>
            نظرة عامة على حالة الطلبات
          </p>
        </div>
        <button
          onClick={fetchDashboard}
          disabled={loading}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            border: `1px solid ${COLORS.border}`,
            borderRadius: '8px',
            backgroundColor: COLORS.white,
            color: COLORS.text,
            fontSize: '14px',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1,
          }}
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          تحديث
        </button>
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <KPICard
          title="جديد اليوم"
          count={data?.kpis?.new_today || 0}
          icon={FileText}
          color={COLORS.info}
          bgColor={COLORS.infoLight}
          active={activeFilter === 'new'}
          onClick={() => handleKPIClick('new')}
        />
        <KPICard
          title="في المجمع"
          count={data?.kpis?.in_pool || 0}
          icon={Inbox}
          color={COLORS.warning}
          bgColor={COLORS.warningLight}
          active={activeFilter === 'pool'}
          onClick={() => handleKPIClick('pool')}
        />
        <KPICard
          title="مُسند"
          count={data?.kpis?.assigned || 0}
          icon={UserCheck}
          color={COLORS.primary}
          bgColor={`${COLORS.primary}15`}
          active={activeFilter === 'assigned'}
          onClick={() => handleKPIClick('assigned')}
        />
        <KPICard
          title="مكتمل اليوم"
          count={data?.kpis?.completed_today || 0}
          icon={CheckCircle}
          color={COLORS.success}
          bgColor={COLORS.successLight}
          active={activeFilter === 'completed'}
          onClick={() => handleKPIClick('completed')}
        />
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '20px',
        alignItems: 'center',
        flexWrap: 'wrap',
      }}>
        <select
          value={filters.program}
          onChange={(e) => {
            setFilters({ ...filters, program: e.target.value });
            setActiveFilter(null);
          }}
          style={{
            padding: '10px 14px',
            border: `1px solid ${COLORS.border}`,
            borderRadius: '8px',
            fontSize: '14px',
            backgroundColor: COLORS.white,
            minWidth: '160px',
          }}
        >
          <option value="">كل البرامج</option>
          {data?.programs?.map(p => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>

        <select
          value={filters.service}
          onChange={(e) => {
            setFilters({ ...filters, service: e.target.value });
            setActiveFilter(null);
          }}
          style={{
            padding: '10px 14px',
            border: `1px solid ${COLORS.border}`,
            borderRadius: '8px',
            fontSize: '14px',
            backgroundColor: COLORS.white,
            minWidth: '180px',
          }}
        >
          <option value="">كل الخدمات</option>
          {data?.services?.map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        <select
          value={filters.status}
          onChange={(e) => {
            setFilters({ ...filters, status: e.target.value });
            setActiveFilter(null);
          }}
          style={{
            padding: '10px 14px',
            border: `1px solid ${COLORS.border}`,
            borderRadius: '8px',
            fontSize: '14px',
            backgroundColor: COLORS.white,
            minWidth: '140px',
          }}
        >
          <option value="">كل الحالات</option>
          {Object.entries(STATUS_CONFIG).map(([key, config]) => (
            <option key={key} value={key}>{config.label}</option>
          ))}
        </select>

        {(filters.program || filters.service || filters.status || activeFilter) && (
          <button
            onClick={() => {
              setFilters({ program: '', service: '', status: '' });
              setActiveFilter(null);
            }}
            style={{
              padding: '10px 14px',
              border: 'none',
              borderRadius: '8px',
              backgroundColor: COLORS.bgDark,
              color: COLORS.textMuted,
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            مسح الفلاتر
          </button>
        )}

        <span style={{ marginRight: 'auto', fontSize: '13px', color: COLORS.textMuted }}>
          {filteredRequests.length} طلب
        </span>
      </div>

      {/* Requests Table */}
      <div style={{
        backgroundColor: COLORS.white,
        borderRadius: '12px',
        border: `1px solid ${COLORS.border}`,
        overflow: 'hidden',
      }}>
        {filteredRequests.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center', color: COLORS.textMuted }}>
            <LayoutDashboard size={48} style={{ marginBottom: '16px', opacity: 0.4 }} />
            <p>لا توجد طلبات</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: COLORS.bg }}>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted }}>
                  رقم الطلب
                </th>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted }}>
                  البرنامج
                </th>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted }}>
                  الخدمة
                </th>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted }}>
                  الحالة
                </th>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted }}>
                  المالك الحالي
                </th>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted }}>
                  الوقت
                </th>
                <th style={{ padding: '14px 16px', textAlign: 'left' }}></th>
              </tr>
            </thead>
            <tbody>
              {filteredRequests.map((req) => (
                <tr
                  key={req.request_id}
                  onClick={() => navigate(`/crm/request/${req.request_id}`)}
                  style={{
                    borderBottom: `1px solid ${COLORS.border}`,
                    cursor: 'pointer',
                    backgroundColor: req.is_delayed ? COLORS.errorLight : 'transparent',
                    transition: 'background-color 0.2s',
                  }}
                  data-testid={`dashboard-row-${req.request_id}`}
                >
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {req.is_delayed && (
                        <AlertTriangle size={16} color={COLORS.error} title="متأخر - أكثر من 24 ساعة" />
                      )}
                      <span style={{ fontSize: '13px', fontWeight: 500, color: COLORS.primary }}>
                        {req.request_id?.substring(0, 16)}...
                      </span>
                    </div>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '6px',
                      backgroundColor: COLORS.bgDark,
                      fontSize: '12px',
                      color: COLORS.text,
                    }}>
                      {req.program_name_ar || '-'}
                    </span>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{ fontSize: '13px', color: COLORS.text }}>
                      {req.service_name_ar || '-'}
                    </span>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <StatusBadge status={req.status} />
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '6px',
                        backgroundColor: COLORS.bgDark,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <User size={14} color={COLORS.textMuted} />
                      </div>
                      <span style={{ fontSize: '13px', color: COLORS.text }}>
                        {req.owner_name || '-'}
                      </span>
                    </div>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Clock size={14} color={req.is_delayed ? COLORS.error : COLORS.textMuted} />
                      <span style={{ 
                        fontSize: '12px', 
                        color: req.is_delayed ? COLORS.error : COLORS.textMuted,
                        fontWeight: req.is_delayed ? 500 : 400,
                      }}>
                        {req.relative_time}
                      </span>
                    </div>
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'left' }}>
                    <ChevronRight size={18} color={COLORS.textMuted} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Delay Legend */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginTop: '16px',
        padding: '12px 16px',
        backgroundColor: COLORS.bgDark,
        borderRadius: '8px',
        fontSize: '13px',
        color: COLORS.textMuted,
      }}>
        <AlertTriangle size={16} color={COLORS.error} />
        <span>الطلبات المتأخرة (أكثر من 24 ساعة) تظهر باللون الأحمر</span>
      </div>
    </div>
  );
}
